import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Tooltip,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OpenClassAssignmentFields, {
  OpenClassAssignmentFormValues,
} from '../../bookingOrders/components/OpenClassAssignmentFields';
import {
  describeOpenClassOrderError,
  getOpenClassOrderSubsidiaryCompanyId,
} from '../../bookingOrders/utils/openClassBookingOrder';
import { useUser } from '../../user/userContext';
import OpenClassAPI from '../OpenClassAPI';
import { useOpenClassOrderDetails } from '../hooks/useOpenClassOrderDetails';
import {
  OpenClass,
  OpenClassBookingOrder,
  OpenClassRegistration,
} from '../types/OpenClass';
import { offeredCertificateType } from '../utils/openClass';
import {
  distinctBookingOrderIds,
  openClassOrderLabel,
} from '../utils/openClassOrderInfo';

type OrderMode = 'existing' | 'new';

type AddAttendeeFormValues = OpenClassAssignmentFormValues & {
  birthDate?: Dayjs;
  birthPlace?: string;
  comment?: string;
  email?: string;
  existingOrderId?: OpenClassBookingOrder['id'];
  name?: string;
  orderMode?: OrderMode;
  phoneNumber?: string;
};

// What already went through, so a retry after a failed step doesn't create a second order
// or a second attendee. Whatever it covers is locked in the form, since a later edit to it
// would not be sent anywhere.
type SaveProgress = {
  orderId?: OpenClassBookingOrder['id'];
  participantId?: number;
};

type AddOpenClassAttendeeModalProps = {
  openClass: OpenClass;
  open: boolean;
  registrations: OpenClassRegistration[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const AddOpenClassAttendeeModal: React.FC<AddOpenClassAttendeeModalProps> = ({
  openClass,
  open,
  registrations,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useUser();
  const [form] = Form.useForm<AddAttendeeFormValues>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [progress, setProgress] = useState<SaveProgress>({});
  // The attendee landed on the order but we cannot name them, so there is nothing to retry
  const [stranded, setStranded] = useState(false);

  const orderIds = useMemo(
    () => distinctBookingOrderIds(registrations),
    [registrations],
  );
  const { failedOrderIds, loading, ordersById } =
    useOpenClassOrderDetails(orderIds);

  const subsidiaryCompanyId = getOpenClassOrderSubsidiaryCompanyId(
    currentUser,
    { isNewOrder: true },
  );

  const orderMode = Form.useWatch('orderMode', form);
  const certificateTemplateId = Form.useWatch('certificateTemplateId', form);
  const furboSelected =
    offeredCertificateType(openClass, certificateTemplateId) === 'furbo';
  const canPickExistingOrder = orderIds.length > 0;
  const canCreateOrder = subsidiaryCompanyId != null;
  const newOrder = orderMode === 'new';
  const orderLocked = progress.orderId != null;
  const attendeeLocked = progress.participantId != null;

  const orderOptions = useMemo(
    () =>
      orderIds.map((orderId) => ({
        label: openClassOrderLabel(
          ordersById.get(orderId)?.order,
          orderId,
          t('components.AddOpenClassAttendeeModal.unnamedOrder'),
        ),
        value: orderId,
      })),
    [orderIds, ordersById, t],
  );

  const createOrder = useCallback(
    async (values: AddAttendeeFormValues) => {
      const { data } = await OpenClassAPI.createStandaloneBookingOrder({
        subsidiaryCompanyId: subsidiaryCompanyId!,
        contact: {
          name: values.name!.trim(),
          email: values.email!.trim(),
          phoneNumber: values.phoneNumber?.trim() || null,
        },
        orderDate: dayjs().format('YYYY-MM-DD'),
        comment: values.comment?.trim() || null,
      });
      return data;
    },
    [subsidiaryCompanyId],
  );

  // The add answers with the whole order, so the new participant is whichever id the order
  // did not have a moment ago.
  const addParticipant = useCallback(
    async (
      orderId: OpenClassBookingOrder['id'],
      knownParticipantIds: Set<number>,
      values: AddAttendeeFormValues,
    ) => {
      const { data } = await OpenClassAPI.addOrderParticipant(orderId, {
        attendee: {
          name: values.name!.trim(),
          email: values.email?.trim() || null,
          birthDate: values.birthDate?.format('YYYY-MM-DD') ?? null,
          birthPlace: values.birthPlace?.trim() || null,
        },
      });
      return data.participants.find(({ id }) => !knownParticipantIds.has(id))
        ?.id;
    },
    [],
  );

  const save = useCallback(
    async (values: AddAttendeeFormValues) => {
      setSaving(true);
      setError(undefined);
      try {
        let { orderId, participantId } = progress;
        let knownParticipantIds: Set<number> | undefined;

        if (orderId == null) {
          if (values.orderMode === 'existing') {
            orderId = values.existingOrderId!;
          } else {
            const created = await createOrder(values);
            orderId = created.order.id;
            knownParticipantIds = new Set(
              created.participants.map(({ id }) => id),
            );
            setProgress({ orderId });
          }
        }

        if (participantId == null) {
          // Read back rather than trusting what the modal opened with, so a concurrent
          // change to the order cannot misname the new participant.
          if (!knownParticipantIds) {
            const { data } = await OpenClassAPI.getBookingOrderDetails(orderId);
            knownParticipantIds = new Set(
              data.participants.map(({ id }) => id),
            );
          }
          participantId = await addParticipant(
            orderId,
            knownParticipantIds,
            values,
          );
          if (participantId == null) {
            setStranded(true);
            setProgress({ orderId });
            setError(
              t('components.AddOpenClassAttendeeModal.attendeeNotMatched'),
            );
            return;
          }
          setProgress({ orderId, participantId });
        }

        await OpenClassAPI.createParticipantRegistration(
          orderId,
          participantId,
          {
            openClassId: openClass.id,
            certificateTemplateId: values.certificateTemplateId!,
            sessionIds: values.sessionIds ?? [],
          },
        );

        message.success(t('components.AddOpenClassAttendeeModal.saved'));
        setProgress({});
        await onSaved();
        onClose();
      } catch (saveError) {
        setError(
          describeOpenClassOrderError(
            saveError,
            t,
            'views.OpenClasses.saveFailed',
          ),
        );
      } finally {
        setSaving(false);
      }
    },
    [addParticipant, createOrder, onClose, onSaved, openClass.id, progress, t],
  );

  const close = useCallback(() => {
    if (progress.orderId != null) onSaved();
    onClose();
  }, [onClose, onSaved, progress.orderId]);

  return (
    <Modal
      footer={[
        <Button key="cancel" disabled={saving} onClick={close}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="submit"
          disabled={stranded || (!canPickExistingOrder && !canCreateOrder)}
          loading={saving}
          type="primary"
          onClick={() => form.submit()}>
          {t('common.save')}
        </Button>,
      ]}
      maskClosable={false}
      open={open}
      title={t('components.AddOpenClassAttendeeModal.title')}
      width={680}
      onCancel={close}>
      <Form
        form={form}
        initialValues={{
          openClassId: openClass.id,
          orderMode: canPickExistingOrder ? 'existing' : 'new',
        }}
        layout="vertical"
        onFinish={save}>
        <Form.Item
          extra={t(
            newOrder
              ? 'components.AddOpenClassAttendeeModal.newOrderHint'
              : 'components.AddOpenClassAttendeeModal.existingOrderHint',
          )}
          name="orderMode"
          rules={[{ required: true }]}>
          <Radio.Group disabled={orderLocked}>
            <Tooltip
              title={
                canPickExistingOrder
                  ? undefined
                  : t('components.AddOpenClassAttendeeModal.noOrdersYet')
              }>
              <Radio.Button disabled={!canPickExistingOrder} value="existing">
                {t('components.AddOpenClassAttendeeModal.existingOrder')}
              </Radio.Button>
            </Tooltip>
            <Tooltip
              title={
                canCreateOrder
                  ? undefined
                  : t(
                      'components.AddOpenClassAttendeeModal.noSubsidiaryCompany',
                    )
              }>
              <Radio.Button disabled={!canCreateOrder} value="new">
                {t('components.AddOpenClassAttendeeModal.newOrder')}
              </Radio.Button>
            </Tooltip>
          </Radio.Group>
        </Form.Item>

        {failedOrderIds.length > 0 && (
          <Alert
            className="mb-4"
            message={t(
              'components.AddOpenClassAttendeeModal.orderDetailsLoadFailed',
              { count: failedOrderIds.length },
            )}
            showIcon
            type="warning"
          />
        )}

        {!newOrder && (
          <Form.Item
            label={t('components.AddOpenClassAttendeeModal.pickOrder')}
            name="existingOrderId"
            rules={[{ required: true }]}>
            <Select
              disabled={orderLocked}
              loading={loading}
              optionFilterProp="label"
              options={orderOptions}
              showSearch
            />
          </Form.Item>
        )}

        <Form.Item
          label={t('common.name')}
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: t(
                'views.OpenClassBookingOrderDetails.attendeeNameRequired',
              ),
            },
          ]}>
          <Input disabled={orderLocked || attendeeLocked} />
        </Form.Item>
        <Form.Item
          label={t('common.email')}
          name="email"
          rules={[
            {
              // A new order is booked in the attendee's name, and that needs an address
              required: newOrder,
              whitespace: true,
              message: t(
                'components.AddOpenClassAttendeeModal.newOrderEmailRequired',
              ),
            },
            {
              type: 'email',
              message: t(
                'views.OpenClassBookingOrderDetails.attendeeEmailInvalid',
              ),
            },
          ]}>
          <Input disabled={orderLocked || attendeeLocked} />
        </Form.Item>
        {newOrder && (
          <Form.Item label={t('common.phoneNumber')} name="phoneNumber">
            <Input disabled={orderLocked} />
          </Form.Item>
        )}
        <Form.Item
          label={t('common.birthDate')}
          name="birthDate"
          rules={[
            {
              required: furboSelected,
              message: t(
                'views.OpenClassBookingOrderDetails.attendeeBirthDetailsRequired',
              ),
            },
          ]}>
          <DatePicker
            className="w-full"
            disabled={attendeeLocked}
            disabledDate={(date) => date.isAfter(dayjs(), 'day')}
          />
        </Form.Item>
        <Form.Item
          label={t('common.birthPlace')}
          name="birthPlace"
          rules={[
            {
              required: furboSelected,
              whitespace: true,
              message: t(
                'views.OpenClassBookingOrderDetails.attendeeBirthDetailsRequired',
              ),
            },
          ]}>
          <Input disabled={attendeeLocked} />
        </Form.Item>

        <OpenClassAssignmentFields
          disableOpenClass
          form={form}
          openClasses={[openClass]}
          retainedOpenClassId={openClass.id}
        />

        {newOrder && (
          <Form.Item label={t('views.OpenClasses.comment')} name="comment">
            <Input.TextArea disabled={orderLocked} rows={2} />
          </Form.Item>
        )}

        {error && (
          <Alert
            description={
              progress.participantId != null
                ? t('components.AddOpenClassAttendeeModal.partialSaveWarning')
                : undefined
            }
            message={error}
            showIcon
            type="error"
          />
        )}
      </Form>
    </Modal>
  );
};

export default AddOpenClassAttendeeModal;
